/* eslint-disable no-unused-vars */
import React, { useContext, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import { useFormik } from "formik";
import moment from "moment";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { clientReport } from "../schemas";
import Swal from "sweetalert2";
import CurrencyFormat from "react-currency-format";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function CafeSalesExpenseReport() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [salesSummaryData, setSalesSummaryData] = useState([]);
  const [show, setShow] = useState(false);

  const date = new Date();
  const defaultValue = date.toLocaleDateString("en-CA");
  const dateSelected = moment(defaultValue).format("YYYY-MM-DD");

  const formik = useFormik({
    initialValues: {
      sdate: dateSelected,
      edate: dateSelected,
    },
    validationSchema: clientReport,
    onSubmit: (values) => {
      generateReport(values);
    },
  });

  const generateReport = async (values) => {
    if (Date.parse(values.sdate) <= Date.parse(values.edate)) {
      setLoading(true);
      try {
        setReportData([]);
        setSummaryData({});

        // Call sales summary API
        const salesResponse = await UserServices.getCafeSummaryReport(branch_id, {
          sdate: values.sdate,
          edate: values.edate
        });

        // Call expenses API - same as view expenses but with category filter
        const expensesResponse = await UserServices.getExpences(
          branch_id,
          9999999999999999999999, // limit
          1, // page
          {
            startdate: values.sdate,
            enddate: values.edate
          }
        );

        if (salesResponse.status === 200 && expensesResponse.status === 200) {
          const salesData = salesResponse.data;
          const expensesData = expensesResponse.data;

          // Filter expenses for cafe category (category_id = 30)
          const cafeExpenses = expensesData.data?.data?.filter(expense => expense.category_id === 30) || [];

          // Process data
          const processedData = processReportData(salesData, cafeExpenses);
          const summary = calculateSummary(processedData);

          setReportData(processedData);
          setSummaryData(summary);
          setSalesSummaryData(salesData.data || []);
          setShow(true);
        } else {
          throw new Error('Failed to fetch data from server');
        }
      } catch (err) {
        console.error("Error generating report:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.message || "Failed to generate report. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date Range",
        text: "End date must be greater than or equal to start date.",
      });
    }
  };

  const processReportData = (salesData, expensesData) => {
    const dateMap = {};

    // Process sales data from new API format
    if (salesData.data && Array.isArray(salesData.data)) {
      salesData.data.forEach(salesItem => {
        const date = salesItem.order_date;
        if (!dateMap[date]) {
          dateMap[date] = {
            date: date,
            sales: {
              order_count: 0,
              total_price: 0,
              total_discount: 0,
              total_tax: 0,
              total_net_price: 0
            },
            expenses: []
          };
        }
        
        dateMap[date].sales.order_count = parseInt(salesItem.order_count || 0);
        dateMap[date].sales.total_price = parseFloat(salesItem.total_price || 0);
        dateMap[date].sales.total_discount = parseFloat(salesItem.total_discount || 0);
        dateMap[date].sales.total_tax = parseFloat(salesItem.total_tax || 0);
        dateMap[date].sales.total_net_price = parseFloat(salesItem.total_net_price || 0);
      });
    }

    // Process expenses data
    if (Array.isArray(expensesData)) {
      expensesData.forEach(expense => {
        const expenseDate = expense.occurrence_date;
        if (!dateMap[expenseDate]) {
          dateMap[expenseDate] = {
            date: expenseDate,
            sales: {
              order_count: 0,
              total_price: 0,
              total_discount: 0,
              total_tax: 0,
              total_net_price: 0
            },
            expenses: []
          };
        }
        dateMap[expenseDate].expenses.push(expense);
      });
    }

    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const calculateSummary = (data) => {
    let totalSales = 0;
    let totalExpenses = 0;
    let totalOrders = 0;

    data.forEach(day => {
      totalSales += parseFloat(day.sales.total_net_price || 0);
      totalExpenses += day.expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
      totalOrders += parseInt(day.sales.order_count || 0);
    });

    return {
      totalSales,
      totalExpenses,
      totalOrders,
      netProfit: totalSales - totalExpenses
    };
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text("Cafe Sales & Expense Report", 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Date Range: ${formik.values.sdate} to ${formik.values.edate}`, 20, 30);
    
    doc.setFontSize(10);
    doc.text(`Total Sales: Rs ${summaryData.totalSales?.toLocaleString() || 0}`, 20, 45);
    doc.text(`Total Expenses: Rs ${summaryData.totalExpenses?.toLocaleString() || 0}`, 20, 55);
    doc.text(`Net Profit: Rs ${summaryData.netProfit?.toLocaleString() || 0}`, 20, 65);
    
    let yPosition = 80;
    doc.text("Date", 20, yPosition);
    doc.text("Sales", 60, yPosition);
    doc.text("Expenses", 100, yPosition);
    doc.text("Net", 140, yPosition);
    
    yPosition += 10;
    
    reportData.forEach(day => {
      const dayExpenses = day.expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
      const netProfit = parseFloat(day.sales.total_net_price || 0) - dayExpenses;
      
      doc.text(moment(day.date).format('DD-MM-YYYY'), 20, yPosition);
      doc.text(`Rs ${parseFloat(day.sales.total_net_price || 0).toLocaleString()}`, 60, yPosition);
      doc.text(`Rs ${dayExpenses.toLocaleString()}`, 100, yPosition);
      doc.text(`Rs ${netProfit.toLocaleString()}`, 140, yPosition);
      
      yPosition += 10;
    });
    
    doc.save(`Cafe_Sales_Expense_Report_${formik.values.sdate}_to_${formik.values.edate}.pdf`);
  };

  const exportToExcel = () => {
    let csvContent = "Date,Sales,Expenses,Net Profit\n";
    
    reportData.forEach(day => {
      const dayExpenses = day.expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
      const netProfit = parseFloat(day.sales.total_net_price || 0) - dayExpenses;
      
      csvContent += `${moment(day.date).format('DD-MM-YYYY')},${day.sales.total_net_price || 0},${dayExpenses},${netProfit}\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Cafe_Sales_Expense_Report_${formik.values.sdate}_to_${formik.values.edate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="row">
            <div className="col-md-12 mb-3">
              <div className="card">
                <div className="card-body">
                  <form className="row g-3" onSubmit={formik.handleSubmit}>
                    <div className="col-12">
                      <h5>Date Range</h5>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-group custom-group">
                        <label className="form-label">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="sdate"
                          value={formik.values.sdate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.errors.sdate && formik.touched.sdate && (
                          <p className="text-danger">{formik.errors.sdate}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-group custom-group">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="edate"
                          value={formik.values.edate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.errors.edate && formik.touched.edate && (
                          <p className="text-danger">{formik.errors.edate}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-group custom-group">
                        <label className="form-label">&nbsp;</label>
                        <button
                          type="submit"
                          className="btn btn-primary w-100"
                          disabled={loading}
                        >
                          {loading ? "Generating..." : "Generate Report"}
                        </button>
                      </div>
                    </div>
                  </form>

                  {show && (
                    <>
                      {/* Summary Cards */}
                      <div className="row mt-4">
                        <div className="col-md-3">
                          <div className="card bg-primary text-white">
                            <div className="card-body">
                              <h5 className="card-title">Total Sales</h5>
                              <h3>
                                <CurrencyFormat
                                  value={summaryData.totalSales || 0}
                                  displayType="text"
                                  thousandSeparator={true}
                                  prefix="Rs "
                                />
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card bg-danger text-white">
                            <div className="card-body">
                              <h5 className="card-title">Total Expenses</h5>
                              <h3>
                                <CurrencyFormat
                                  value={summaryData.totalExpenses || 0}
                                  displayType="text"
                                  thousandSeparator={true}
                                  prefix="Rs "
                                />
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card bg-success text-white">
                            <div className="card-body">
                              <h5 className="card-title">Net Profit</h5>
                              <h3>
                                <CurrencyFormat
                                  value={summaryData.netProfit || 0}
                                  displayType="text"
                                  thousandSeparator={true}
                                  prefix="Rs "
                                />
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="card bg-info text-white">
                            <div className="card-body">
                              <h5 className="card-title">Total Orders</h5>
                              <h3>{summaryData.totalOrders || 0}</h3>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Export Buttons */}
                      <div className="row mt-3">
                        <div className="col-12">
                          <button
                            className="btn btn-success me-2"
                            onClick={exportToPDF}
                          >
                            Export PDF
                          </button>
                          <button
                            className="btn btn-warning"
                            onClick={exportToExcel}
                          >
                            Export Excel
                          </button>
                        </div>
                      </div>

                      {/* Report Table */}
                      <div className="row mt-4">
                        <div className="col-12">
                          <div className="table-responsive">
                            <table className="table table-striped table-bordered">
                              <thead className="table-dark">
                                <tr>
                                  <th>Date</th>
                                  <th>Sales Summary</th>
                                  <th>Expenses Details</th>
                                  <th>Net Profit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportData.map((day, index) => {
                                  const dayExpenses = day.expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
                                  const netProfit = parseFloat(day.sales.total_net_price || 0) - dayExpenses;
                                  
                                  return (
                                    <tr key={index}>
                                      <td>{moment(day.date).format('DD-MM-YYYY')}</td>
                                      <td>
                                        <div>
                                          <strong>Orders: {day.sales.order_count}</strong><br/>
                                          <strong>Tax: </strong>
                                          <CurrencyFormat
                                            value={day.sales.total_tax || 0}
                                            displayType="text"
                                            thousandSeparator={true}
                                            prefix="Rs "
                                          /><br/>
                                          <strong>Discount: </strong>
                                          <CurrencyFormat
                                            value={day.sales.total_discount || 0}
                                            displayType="text"
                                            thousandSeparator={true}
                                            prefix="Rs "
                                          /><br/>
                                          <strong>Total: </strong>
                                          <CurrencyFormat
                                            value={day.sales.total_net_price || 0}
                                            displayType="text"
                                            thousandSeparator={true}
                                            prefix="Rs "
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        {day.expenses.length > 0 ? (
                                          <div>
                                            <strong>Total: </strong>
                                            <CurrencyFormat
                                              value={dayExpenses}
                                              displayType="text"
                                              thousandSeparator={true}
                                              prefix="Rs "
                                            /><br/>
                                            <div className="mt-2">
                                              {day.expenses.map((expense, expIndex) => (
                                                <div key={expIndex} className="small">
                                                  <strong>{expense.sub_category_name}:</strong> 
                                                  <CurrencyFormat
                                                    value={expense.amount}
                                                    displayType="text"
                                                    thousandSeparator={true}
                                                    prefix=" Rs "
                                                  />
                                                  <br/>
                                                  <em>{expense.description}</em>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="text-muted">No expenses</span>
                                        )}
                                      </td>
                                      <td>
                                        <CurrencyFormat
                                          value={netProfit}
                                          displayType="text"
                                          thousandSeparator={true}
                                          prefix="Rs "
                                          className={netProfit >= 0 ? "text-success" : "text-danger"}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
}
